import axios from "axios";

import { useErrorStore } from "@/stores/errorStore";

import { getApiBaseUrl } from "@/utils/apiUrlUtils";
import { getCookie } from "@/utils/cookieUtils";

class Api {
  constructor() {
    this.baseURL = `${getApiBaseUrl()}/api/v1/`;
    this.token = getCookie("token");
    this.networkErrorToastTimer = null;

    // Инициализируем axios instance для перехвата ошибок
    this.axiosInstance = axios.create({
      baseURL: this.baseURL,
    });

    // Добавляем интерсептор для ошибок
    this.axiosInstance.interceptors.response.use(
      (response) => this.handleResponse(response),
      (error) => {
        this.handleError(error);
        return Promise.reject(error);
      }
    );
  }

  handleResponse(response) {
    this.clearNetworkErrorToastTimer();
    return response;
  }

  handleError(error) {
    if (error.code === "ERR_CANCELED") return;
    // Фоновая синхронизация показывает постоянный статус вместо повторных toast.
    // Истечение сессии по-прежнему обрабатывается общим механизмом авторизации.
    if (error.config?.skipErrorNotification) {
      if (error.response?.status === 401) {
        window.dispatchEvent(new CustomEvent("auth:unauthorized"));
      }
      return;
    }
    const errorStore = useErrorStore();

    if (this.isBrowserOfflineNetworkError(error)) {
      return;
    }

    if (!error.response) {
      this.scheduleNetworkErrorToast(errorStore);
      return;
    }

    this.clearNetworkErrorToastTimer();

    const { status, data } = error.response;

    if (status === 401) {
      window.dispatchEvent(new CustomEvent("auth:unauthorized"));
    }

    let backendMessage = null;

    if (data?.errors) {
      if (typeof data.errors === "string") {
        backendMessage = data.errors;
      } else if (
        Array.isArray(data.errors) &&
        data.errors.length > 0
      ) {
        backendMessage = data.errors[0];
      }
    }

    if (!backendMessage && data?.message) {
      backendMessage = data.message;
    }

    errorStore.setError({
      message: backendMessage || this.getDefaultErrorMessage(status),
      code: status,
      details: data?.errors,
    });
  }

  isBrowserOfflineNetworkError(error) {
    // На мобильных браузер может открыть приложение в момент краткого offline.
    // Такой сетевой флап не должен сразу выглядеть для пользователя как ошибка приложения.
    return !error.response && window.navigator.onLine === false;
  }

  scheduleNetworkErrorToast(errorStore) {
    this.clearNetworkErrorToastTimer();

    this.networkErrorToastTimer = window.setTimeout(() => {
      if (window.navigator.onLine === false) return;

      errorStore.setError({
        message:
          "Ошибка сети: Пожалуйста, проверьте интернет-соединение",
        code: "NETWORK_ERROR",
      });
    }, 3000);
  }

  clearNetworkErrorToastTimer() {
    if (!this.networkErrorToastTimer) return;

    window.clearTimeout(this.networkErrorToastTimer);
    this.networkErrorToastTimer = null;
  }

  getDefaultErrorMessage(status) {
    const messages = {
      400: "Некорректный запрос",
      401: "Требуется авторизация",
      403: "Доступ запрещен",
      404: "Ресурс не найден",
      500: "Внутренняя ошибка сервера",
      default: "Произошла ошибка",
    };

    return messages[status] || messages.default;
  }

  updateToken(token) {
    this.token = token;
  }

  getRequest(path, params, headers, config) {
    return this.axiosInstance.get(path, {
      params,
      headers: { ...this.authHeaders(), ...headers },
      ...config, // позволяет передавать responseType, timeout и др.
    });
  }

  getPublicRequest(path, params = {}, config = {}) {
    return this.axiosInstance.get(path, {
      params,
      ...config,
    });
  }

  postRequest(path, data, headers, config = {}) {
    return this.axiosInstance.post(path, data, {
      ...config,
      headers: {
        ...this.authHeaders(),
        ...headers,
        ...(config.headers || {}),
      },
    });
  }

  patchRequest(path, data, headers) {
    return this.axiosInstance.patch(path, data, {
      headers: { ...this.authHeaders(), ...headers },
    });
  }

  deleteRequest(path, headers) {
    return this.axiosInstance.delete(path, {
      headers: { ...this.authHeaders(), ...headers },
    });
  }

  authHeaders() {
    return {
      Authorization: `Basic ${this.token || getCookie("token")}`,
    };
  }

  // authHeadersVehiclesEdit() {
  //   return {
  //     "Content-Type": "multipart/form-data",
  //     Authorization: `Basic ${this.token || getCookie("token")}`,
  //   };
  // }
  static postRequest(url, data, headers = {}) {
    return axios.post(this.getFullUrl(url), data, { headers });
  }

  methodPath(path) {
    return this.baseURL + path;
  }
}

export default new Api();
